import RACAccountBUForm from "@/apps/tatar/invoiceConfigApp/views/AccountingConfig/RACAccountBUForm";
import RACAccountForm from "@/apps/tatar/invoiceConfigApp/views/AccountingConfig/RACAccountForm";
import AssetLoader from "@/components/AssetLoader/AssetLoader";
import DependingCalculationField from "@/components/Form/Fields/DependingCalculationField";
import FormFieldValues from "@/components/Form/Fields/FormFieldValues";
import FormStruct from "@/components/Form/FormStruct/FormStruct";
import { FV } from "@/components/Form/Validation/FormValidators";
import ModalManager from "@/components/ModalComponent/ModalManager";
import Log from "@/debug/Log";
import i18n from "@/i18n";
import { AssetTypes } from "@/model/AssetTypes";
import { Contact } from "@/model/db/Contact";
import { ObjectId } from "@/model/db/General";
import BFAssetChooser from "@/modules/abstract-ui/forms/chooser/BFAssetChooser";
import BFDatefield from "@/modules/abstract-ui/forms/datefield/BFDatefield";
import BFInput from "@/modules/abstract-ui/forms/input/BFInput";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import BFMessageBox from "@/modules/abstract-ui/general/Message/BFMessageBox";
import BFOverlay from "@/modules/abstract-ui/general/whisper/BFOverlay";
import BfIcon from "@/modules/abstract-ui/icon/BfIcon";
import { DefaultIcons } from "@/modules/abstract-ui/icon/DefaultIcons";
import BFPageSwitch from "@/modules/abstract-ui/layout/page-switch/BFPageSwitch";
import { patchAssetData } from "@/redux/actions/application/application-actions";
import InvoiceStruct from "@/redux/actions/struct/implemented/InvoiceStruct";
import OrgaStruct from "@/redux/actions/struct/implemented/OrgaStruct";
import CacheService from "@/services/CacheService";
import PermissionService from "@/services/PermissionService";
import NumberUtils from "@/utils/ NumberUtils";
import { when } from "@/utils/Helpers";
import MQ from "@/utils/MatchQueryUtils";
import StringUtils from "@/utils/StringUtils";
import classNames from "classnames";
import { FormApi } from "final-form";
import { useEffect, useState } from "react";
import { Field, FormSpy } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { Trans } from "react-i18next";
import { useDispatch } from "react-redux";
import { Accordion, Loader } from "rsuite";
import InvoiceService from "../../InvoiceService";
import {
  BookInvoiceFormData,
  Invoice,
  InvoiceAccount,
  InvoiceAccountBU,
  InvoiceAccountDataCategory,
  InvoiceAccountingRule,
  InvoiceBookingEntryForm,
  InvoiceInstallmentForm,
  PaymentEntry,
  RAInvoiceCostCenter,
} from "../../RAInterfaces";
import RADeclineInvoiceForm from "../DeclineInvoice/RADeclineInvoiceForm";
import "./RABookInvoiceForm.scss";

interface RABookInvoiceFormProps {
  asset: Invoice;

  prefix?: string;
  onNext?: () => void;
  onFinish?: () => void;
  onClose?: () => void;
}

export const getTemplateValuesBookInvoice = (asset: Invoice) => {
  const object = OrgaStruct.getObject(asset.data.objectId);
  const entity = OrgaStruct.getEntity(asset.data.entity);

  const mode = InvoiceStruct.getModeOfPayment(
    asset.data.direction,
    asset?.data?.invoice?.paymentType
  );

  return {
    object: {
      id: object?.id,
      name: object?.displayName,
    },
    entity: {
      id: entity?.id,
      name: entity?.displayName,
    },
    contact: {
      name: asset.nested?.contactDisplayName,
    },
    invoice: {
      invoiceId: asset.data?.invoice.invoiceId,
      contactDisplayName: asset?.nested?.contactDisplayName,
      paymentType: {
        id: mode?.id,
        name: mode?.displayName,
      },
      invoiceType: InvoiceStruct.getInvoiceType(asset.data.invoice?.invoiceType)
        ?.data.displayName,
    },
  };
};

export const RABookInvoiceForm = (props: RABookInvoiceFormProps) => {
  return (
    <AssetLoader
      assetType={AssetTypes.Invoicing.Account}
      query={MQ.and(
        MQ.eq("data.entity", props.asset.data.entity),
        MQ.eq("data.direction", props.asset.data.direction),
        MQ.eq("data.contactId", props.asset.data.invoice.contact)
      )}
      render={(rule: InvoiceAccountingRule) => (
        <FormContent {...props} rule={rule} />
      )}
    />
  );
};

const getInitialValues = (
  asset: Invoice,
  contact: Contact,
  prefix?: string
) => {
  return getInitialValuesBookForm(
    asset,
    contact,
    asset.data.costCenter,
    asset.data.installment,
    prefix
  );
};
export const getInitialValuesBookForm = (
  asset: Invoice,
  contact: Contact,
  costCenter: RAInvoiceCostCenter[],
  installment: PaymentEntry[],
  prefix?: string
) => {
  const rule = InvoiceStruct.getAccountingRule(
    asset.data.type,
    asset.data.direction
  );

  const accountNumber = contact?.data?.contactOverwrite?.find(
    (e) => e.entity === asset.data.entity
  )?.[asset.data.direction === "INCOMING" ? "invoice" : "bill"]?.accountNumber;

  const templateValues = getTemplateValuesBookInvoice(asset);
  return {
    [`${prefix || ""}costCenter`]: costCenter,
    [`${prefix || ""}contactAccount`]: accountNumber,
    [`${prefix || ""}installment`]: installment?.map((i, index) => ({
      amount: i.amount,
      date: i.date,
      referenceId: `${StringUtils.formatDate(i.date, "YYYYMMDD")}_${index}`,
      entries: [
        {
          BU: null,
          BUCode: "",
          accountNumberCode: "",
          accountNumber: null,
          referenceField: rule?.referenceField
            ? StringUtils.template(rule.referenceField, templateValues)
            : "",
          date: i.date,
          serviceDate: null,
          usage: rule?.usage
            ? StringUtils.template(rule.usage, templateValues)
            : "",
          cost1: rule?.cost1
            ? StringUtils.template(rule.cost1, templateValues)
            : "",
          cost2: rule?.cost2
            ? StringUtils.template(rule.cost2, templateValues)
            : "",
          extraInfo: rule?.extraInfo
            ? rule.extraInfo?.map((e) =>
                e ? StringUtils.template(e, templateValues) : ""
              )
            : [],
          value: i.amount,
        },
      ],
    })),
    [`${prefix || ""}paymentPlan`]:
      costCenter?.length > 0
        ? costCenter?.map((c) => ({
            BU: null,
            BUCode: "",
            accountNumberCode: "",
            accountNumber: null,
            referenceField: rule?.referenceField
              ? StringUtils.template(rule.referenceField, templateValues)
              : "",
            date: asset.data.invoice.documentDate,
            serviceDate: null,
            usage: rule?.usage
              ? StringUtils.template(rule.usage, templateValues)
              : "",
            cost1: rule?.cost1
              ? StringUtils.template(rule.cost1, templateValues)
              : "",
            cost2: rule?.cost2
              ? StringUtils.template(rule.cost2, templateValues)
              : "",
            extraInfo: rule?.extraInfo
              ? rule.extraInfo?.map((e) =>
                  e ? StringUtils.template(e, templateValues) : ""
                )
              : [],
            value: c.value,
          }))
        : [
            {
              BU: null,
              BUCode: "",
              accountNumberCode: "",
              accountNumber: null,
              referenceField: rule?.referenceField
                ? StringUtils.template(rule.referenceField, templateValues)
                : "",
              date: asset.data.invoice.documentDate,
              serviceDate: null,
              usage: rule?.usage
                ? StringUtils.template(rule.usage, templateValues)
                : "",
              cost1: rule?.cost1
                ? StringUtils.template(rule.cost1, templateValues)
                : "",
              cost2: rule?.cost2
                ? StringUtils.template(rule.cost2, templateValues)
                : "",
              extraInfo: rule?.extraInfo
                ? rule.extraInfo?.map((e) =>
                    e ? StringUtils.template(e, templateValues) : ""
                  )
                : [],
              value: asset.data.invoice.value.amount,
            },
          ],
  } as BookInvoiceFormData;
};
const FormContent = (
  props: RABookInvoiceFormProps & { rule?: InvoiceAccountingRule }
) => {
  return (
    <AssetLoader
      assetType={AssetTypes.Contact}
      id={props.asset.data.invoice.contact}
      render={(contact: Contact) => {
        return <RABookInvoiceFormContent {...props} contact={contact} />;
      }}
    />
  );
};

export default RABookInvoiceForm;

const RABookInvoiceFormContent = (
  props: RABookInvoiceFormProps & {
    rule?: InvoiceAccountingRule;
    contact: Contact;
  }
) => {
  const dispatch = useDispatch();
  const [declineModalOpen, setDeclineModalOpen] = useState<boolean>(false);
  // "cost2": "Objekt {{object.id}}",
  // "cost1": "{{invoice.invoiceId}}",
  // "extraInfo": ["{{entity.name}}"],
  //  "usage": "{{contact.name}}"

  const [initial, setInitial] = useState<BookInvoiceFormData>(
    getInitialValues(props.asset, props.contact, props.prefix)
  );

  useEffect(() => {
    setInitial(getInitialValues(props.asset, props.contact, props.prefix));
  }, [props.asset]);

  return (
    <BFPageSwitch
      className={`ra-book-invoice-form-root`}
      relativeToFirstPage
      switch={declineModalOpen}
      renderPage1={(active) => (
        <FormStruct<BookInvoiceFormData>
          className={`ra-book-invoice-form`}
          initialValues={initial}
          notModal
          noPadding
          onAbort={props.onClose}
          onSubmit={async (values) => {
            await InvoiceService.bookInvoice(
              props.asset._id,
              values,
              values.contactAccount !== initial.contactAccount
            );

            props.onNext?.();
            props.onFinish?.();
          }}
          submitText={i18n.t(
            "ra:RABookInvoiceForm.markAsBooked",
            "Als gebucht markieren"
          )}
          additionalActions={
            <>
              <BFButton
                disabled={props.asset.data.status === "declined"}
                tooltip={
                  props.asset.data.status === "declined"
                    ? {
                        tooltip: i18n.t(
                          "ra:RABookInvoiceForm.alreadyDeclined",
                          "Rechnung bereits abgelehnt"
                        ),
                      }
                    : undefined
                }
                onClick={() => {
                  setDeclineModalOpen(true);
                }}
                appearance="outline"
              >
                {i18n.t("ra:RABookInvoiceForm.decline", "Ablehnen")}
              </BFButton>
            </>
          }
          render={(formProps) => (
            <>
              <div className={`form-title __h3 __padding-bottom-20`}>
                {i18n.t(
                  "ra:RABookInvoiceForm.bookInvoice",
                  "Buchungserfassung"
                )}
              </div>
              <RABookInvoicesFields
                {...props}
                amount={props.asset.data.invoice.value.amount}
                currency={props.asset.data.invoice.value.currency}
                formProps={formProps.form}
                entity={props.asset.data.entity}
              />
            </>
          )}
        />
      )}
      renderPage2={(active) => (
        <div className={`decline-overlay ${declineModalOpen ? "active" : ""}`}>
          <RADeclineInvoiceForm
            invoiceId={props.asset?._id}
            task={!!props.asset}
            // initialComment={comment}
            // onCommentChange={setComment}
            onClose={() => setDeclineModalOpen(false)}
            selected={true}
            direction={props.asset?.data.direction}
            kind={props.asset?.data.kind}
            type={props.asset?.data.type}
            onFinish={() => {
              dispatch(
                patchAssetData(props.asset?._id, {
                  _declined: true,
                })
              );
              setDeclineModalOpen(false);

              // props.onNext?.();
            }}
          />
        </div>
      )}
    />
  );
};

export const RABookInvoicesFields = (
  props: RABookInvoiceFormProps & {
    rule?: InvoiceAccountingRule;
    contact: Contact;
    formProps: FormApi<BookInvoiceFormData>;
    entity: string;
    amount: number;
    currency: string;
  }
) => {
  return (
    <div className={`ra-book-invoice-form-fields`}>
      <div className={`__flex`}>
        <div className={`__flex-1 `}>
          <FormFieldValues names={[`${props.prefix || ""}costCenter`]}>
            {([costCenter]) => (
              <div className={`__field`}>
                <BFInput
                  value={props.amount}
                  prefix={i18n.t(
                    "ra:RABookInvoiceForm.invoiceAmount",
                    "Rechnungsbetrag"
                  )}
                  type="priceInput"
                  readonly
                  textAlign="right"
                  suffix={StringUtils.getCurrencySymbol(props.currency)}
                  onChange={() => {}}
                />
                {costCenter?.length === 0 ? (
                  <div className={`no-costcenter`}>
                    {i18n.t(
                      "ra:RABookInvoiceForm.noCostCenter",
                      "Keine Unterkategorien hinterlegt"
                    )}
                  </div>
                ) : (
                  <div className={`costcenters`}>
                    <table>
                      <tbody>
                        <tr>
                          <th colSpan={3} className={`__text-align-left`}>
                            {i18n.t(
                              "ra:RABookInvoiceForm.costCenter",
                              "Unterkategorie"
                            )}
                          </th>
                        </tr>
                        {costCenter?.map((c) => (
                          <>
                            <tr>
                              <td>
                                {InvoiceStruct.getInvoiceCostCenter(
                                  c.costCenterId
                                )?.displayName || "-"}
                              </td>
                              <td className={`__sub-text`}>
                                {c.objectId
                                  ? OrgaStruct.getObject(c.objectId)
                                      ?.displayName
                                  : ""}
                              </td>
                              <td className={`__text-align-right`}>
                                {StringUtils.formatCurrency(
                                  c.value,
                                  undefined,
                                  undefined,
                                  props.currency
                                )}
                              </td>
                            </tr>
                            {c.info && (
                              <tr>
                                <td
                                  className={`__sub-text padding-bottom-10`}
                                  colSpan={3}
                                >
                                  {c.info}
                                </td>
                              </tr>
                            )}
                          </>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </FormFieldValues>
        </div>
        <div className={`__flex-1 `}>
          <div className={``}>
            <BFInput
              prefix={i18n.t(
                "ra:RABookInvoiceForm.contact",
                "Geschäftspartner"
              )}
              noBorderRadiusBottom
              value={props.contact.data.displayName}
              readonly
            />
          </div>
          <div className={`__field`}>
            <Field
              name={`${props.prefix || ""}contactAccount`}
              validate={FV.compose(FV.required())}
            >
              {({ input, meta }) => (
                <BFInput
                  noBorderRadiusTop
                  placeholder={i18n.t(
                    "ra:RABookInvoiceForm.noAccountNumber",
                    "Keine Kontonummer hinterlegt"
                  )}
                  prefix={i18n.t("ra:RABookInvoiceForm.accountLabel", "Konto")}
                  {...input}
                  {...FV.getValidation(meta)}
                />
              )}
            </Field>
          </div>
        </div>
      </div>
      <div className={`__h4 padding-top-20 padding-bottom-10`}>
        {i18n.t(
          "ra:RABookInvoiceForm.invoiceAmountSplit",
          "Rechnungsbetrag buchen"
        )}
      </div>
      <BookingPayments
        name={`${props.prefix || ""}paymentPlan`}
        asset={props.asset}
        formProps={props.formProps}
        baseAmount={props.amount}
        entity={props.entity}
      />
      <Field
        name={`${props.prefix || ""}paymentPlanSumValidation`}
        validate={(value, allValues) => {
          const valuesRoot =
            (props.prefix
              ? allValues[props.prefix.substring(0, props.prefix.length - 1)]
              : allValues) || {};

          return NumberUtils.normalizeDecimal(
            _.sum(valuesRoot["paymentPlan"]?.map((e) => e.value || 0))
          ) !== NumberUtils.normalizeDecimal(props.amount) ? (
            <Trans i18nKey="ra:RABookInvoiceForm.sumValidationWithExplaination">
              Die Summe der Buchung muss dem Rechnungsbetrag entsprechen.
              Aktuell sind insgesamt{" "}
              <span className="weight-600">
                {{
                  sum: StringUtils.formatCurrency(
                    _.sum(valuesRoot["paymentPlan"]?.map((e) => e.value || 0)),
                    undefined,
                    undefined,
                    props.currency
                  ),
                }}{" "}
                gebucht,
              </span>{" "}
              es sind noch{" "}
              <span className="weight-600">
                {{
                  missing: StringUtils.formatCurrency(
                    props.amount -
                      _.sum(
                        valuesRoot["paymentPlan"]?.map((e) => e.value || 0)
                      ),
                    undefined,
                    undefined,
                    props.currency
                  ),
                }}{" "}
                offen.
              </span>
            </Trans>
          ) : undefined;
        }}
      >
        {({ input, meta }) =>
          meta.error ? (
            <BFMessageBox type="error">{meta.error}</BFMessageBox>
          ) : null
        }
      </Field>

      <BookingInstallments
        entity={props.entity}
        amount={props.amount}
        currency={props.currency}
        prefix={props.prefix}
        asset={props.asset}
        formProps={props.formProps}
      />
    </div>
  );
};

const BookingPayments = (props: {
  name: string;
  formProps: FormApi<BookInvoiceFormData>;
  asset: Invoice;
  baseAmount: number;
  entity: string;
}) => {
  const { entity } = props;
  const disableEditAccounting =
    Boolean(
      PermissionService.hasBusinessUnitRole("invConf_accounting", false, false)
    ) === false;
  return (
    <>
      <FieldArray<InvoiceBookingEntryForm> name={props.name}>
        {({ fields }) => {
          const openAmount =
            props.baseAmount - _.sum((fields.value || []).map((f) => f.value));
          return (
            <>
              <div className={`entries`}>
                {fields?.map((name, index) => {
                  return (
                    <div className={`entry`} key={index}>
                      <Field
                        name={`${name}.value`}
                        validate={FV.compose(FV.required())}
                      >
                        {({ input, meta }) => (
                          <BFInput
                            className={`first-row`}
                            noBorderRadiusBottom
                            prefix={
                              <div className={`__flex`}>
                                {fields.length > 1 && (
                                  <BFButton
                                    className={`margin-right-10`}
                                    inline
                                    appearance="link"
                                    // size={"xxs"}
                                    noPadding
                                    onClick={() => fields.remove(index)}
                                  >
                                    <BfIcon
                                      {...DefaultIcons.CLOSE}
                                      size="xxs"
                                    />
                                  </BFButton>
                                )}
                                <div className={`__flex-1`}>
                                  {i18n.t(
                                    "ra:RABookInvoiceForm.amount",
                                    "Betrag"
                                  ) +
                                    (fields.length > 1
                                      ? " " + (index + 1)
                                      : "")}
                                </div>

                                {openAmount !== 0 && (
                                  <BFButton
                                    onClick={() => {
                                      input.onChange(input.value + openAmount);
                                    }}
                                    appearance="link"
                                    size="xxs"
                                    noPadding
                                    inline
                                    tooltip={{
                                      tooltip: i18n.t(
                                        "ra:RABookInvoiceForm.setToOpenAmount",
                                        "Offenen Betrag hinzufügen"
                                      ),
                                    }}
                                  >
                                    <BfIcon
                                      data={"flash"}
                                      type="light"
                                      size="xxs"
                                    />
                                  </BFButton>
                                )}
                              </div>
                            }
                            type="priceInput"
                            textAlign="right"
                            suffix={StringUtils.getCurrencySymbol(
                              props.asset.data.invoice.value.converted?.currency
                            )}
                            {...input}
                            {...FV.getValidation(meta)}
                          />
                        )}
                      </Field>

                      <Field
                        name={`${name}.accountNumberCode`}
                        validate={FV.compose(FV.required())}
                      >
                        {({ input, meta }) => (
                          <>
                            <BFInput
                              noBorderRadiusBottom
                              noBorderRadiusTop
                              prefix={i18n.t(
                                "ra:RABookInvoiceForm.accountNumber",
                                "Buchungskonto"
                              )}
                              removeSuffixPadding
                              suggestionHeader={
                                <div className={`__h3`}>
                                  {i18n.t(
                                    "ra:RABookInvoiceForm.accountNumberSuggestion",
                                    "Letzten gebuchte Konten"
                                  )}
                                </div>
                              }
                              suggestions={async () => {
                                const accountRule = (await CacheService.query(
                                  AssetTypes.Invoicing.BookingRule,
                                  MQ.and(
                                    MQ.eq("data.entity", entity),
                                    MQ.eq(
                                      "data.direction",
                                      props.asset.data.direction
                                    ),
                                    MQ.eq(
                                      "data.contact",
                                      props.asset.data.invoice.contact
                                    )
                                  ),
                                  true,
                                  false,
                                  ["data.accounts"]
                                )) as InvoiceAccountingRule;

                                Log.info("Account suggestions:", accountRule);

                                return (
                                  accountRule?.expand?.accounts?.map(
                                    (account) => ({
                                      value: account.data.accountNumber,
                                      label:
                                        account.data.accountNumber +
                                        " - " +
                                        account.data.description,
                                    })
                                  ) || []
                                );
                              }}
                              suffix={
                                <BFOverlay
                                  trigger="click"
                                  enterable
                                  placement="bottomEnd"
                                  speakerFC={(close) => (
                                    <div>
                                      <BFAssetChooser
                                        height={500}
                                        // value={input.value}
                                        focusOnMount
                                        assetType={AssetTypes.Invoicing.Account}
                                        matchQuery={MQ.and(
                                          MQ.eq("data.entity", entity)
                                        )}
                                        groups={[
                                          {
                                            matchQuery: MQ.eq(
                                              "data.category",
                                              InvoiceAccountDataCategory.GENERAL_LEDGER
                                            ),
                                            label: i18n.t(
                                              "ra:RABookInvoiceForm.generalLedger",
                                              "Sachkonto"
                                            ),
                                            value:
                                              InvoiceAccountDataCategory.GENERAL_LEDGER,
                                          },
                                          {
                                            matchQuery: MQ.eq(
                                              "data.category",
                                              InvoiceAccountDataCategory.DEBTOR
                                            ),
                                            label: i18n.t(
                                              "ra:RABookInvoiceForm.debitor",
                                              "Debitoren"
                                            ),
                                            value:
                                              InvoiceAccountDataCategory.DEBTOR,
                                          },
                                          {
                                            matchQuery: MQ.eq(
                                              "data.category",
                                              InvoiceAccountDataCategory.CREDITOR
                                            ),
                                            label: i18n.t(
                                              "ra:RABookInvoiceForm.creditor",
                                              "Kreditoren"
                                            ),
                                            value:
                                              InvoiceAccountDataCategory.CREDITOR,
                                          },
                                          {
                                            matchQuery: MQ.eq(
                                              "data.category",
                                              InvoiceAccountDataCategory.NONE
                                            ),
                                            label: i18n.t(
                                              "ra:RABookInvoiceForm.none",
                                              "Sonstiges"
                                            ),
                                            value:
                                              InvoiceAccountDataCategory.NONE,
                                          },
                                        ]}
                                        getOption={(node: InvoiceAccount) => ({
                                          label: node.data.description,
                                          value: node._id,
                                          subLabel: node.data.accountNumber,
                                        })}
                                        onChange={(
                                          value,
                                          asset: InvoiceAccount
                                        ) => {
                                          input.onChange(
                                            asset.data.accountNumber
                                          );
                                          close();
                                        }}
                                      />
                                    </div>
                                  )}
                                >
                                  <BFButton
                                    appearance="link"
                                    size="xxs"
                                    onClick={() => {}}
                                  >
                                    <BfIcon
                                      {...DefaultIcons.SEARCH}
                                      size="xxs"
                                    />
                                  </BFButton>
                                </BFOverlay>
                              }
                              {...input}
                            />
                          </>
                        )}
                      </Field>

                      <div className={`additional-info-row`}>
                        <div className={`left`} />
                        <div className={`additiona-info-value`}>
                          <DependingCalculationField
                            validate={FV.compose(FV.required())}
                            throttle={500}
                            dependingField={`${name}.accountNumberCode`}
                            name={`${name}.accountNumber`}
                            calulationPromise={async (value) => {
                              const invoiceAccount =
                                (await CacheService.getData({
                                  assetType: AssetTypes.Invoicing.Account,
                                  query: MQ.and(
                                    MQ.eq("data.accountNumber", value),
                                    MQ.eq("data.entity", entity)
                                  ),
                                  forceReload: true,
                                  oType: "asset",
                                })) as InvoiceAccount;

                              if (invoiceAccount) {
                                if (invoiceAccount.data.defaultBU) {
                                  const invoiceBU = await CacheService.getData({
                                    assetType: AssetTypes.Invoicing.AccountBU,
                                    id: invoiceAccount.data.defaultBU,
                                    oType: "asset",
                                  });
                                  if (invoiceBU) {
                                    props.formProps.change(
                                      `${name}.BUCode`,
                                      invoiceBU.data.accountNumber
                                    );
                                  }
                                }
                                return {
                                  value: invoiceAccount._id,
                                  data: invoiceAccount,
                                };
                              } else {
                                throw new Error(
                                  i18n.t(
                                    "ra:RABookInvoiceForm.AccountNumberError",
                                    "Buchungskonto nicht gefunden"
                                  )
                                );
                              }
                            }}
                            renderLoading={() => <Loader size="xs" />}
                            renderError={(error, value: string, reload) => (
                              <div className={`__error`}>
                                {error?.message || "error"}

                                <BFButton
                                  disabled={disableEditAccounting}
                                  tooltip={
                                    disableEditAccounting
                                      ? {
                                          tooltip: i18n.t(
                                            "ra:RABookInvoiceForm.disabledNoPermissionAccouns",
                                            "Sie haben keine Berechtigung, um Buchungskonten zu erstellen"
                                          ),
                                        }
                                      : undefined
                                  }
                                  appearance="link"
                                  noPadding
                                  size="xs"
                                  onClick={() => {
                                    ModalManager.show({
                                      noPadding: true,
                                      size: "sm",
                                      content: (state, setState, close) => (
                                        <RACAccountForm
                                          direction={props.asset.data.direction}
                                          entityId={entity}
                                          onClose={close}
                                          onSubmit={(node: InvoiceAccount) => {
                                            props.formProps.change(
                                              `${name}.accountNumberCode`,
                                              node.data.accountNumber
                                            );
                                            props.formProps.change(
                                              `${name}.accountNumber`,
                                              node._id
                                            );
                                            close();
                                            reload();
                                          }}
                                          initialValues={{
                                            accountNumber: value,
                                          }}
                                        />
                                      ),
                                    });
                                  }}
                                >
                                  {i18n.t(
                                    "Global.Buttons.establish",
                                    "Anlegen"
                                  )}
                                </BFButton>
                              </div>
                            )}
                            renderValue={(
                              value: any,
                              data: InvoiceAccount,
                              reload
                            ) => (
                              <div className={`__flex full-width`}>
                                <div className={`__flex-1`}>
                                  {data.data.description}
                                </div>

                                <BFButton
                                  disabled={disableEditAccounting}
                                  tooltip={
                                    disableEditAccounting
                                      ? {
                                          tooltip: i18n.t(
                                            "ra:RABookInvoiceForm.disabledNoPermissionAccouns",
                                            "Sie haben keine Berechtigung, um Buchungskonten zu erstellen"
                                          ),
                                        }
                                      : undefined
                                  }
                                  appearance="link"
                                  noPadding
                                  className={`padding-5`}
                                  size="xs"
                                  onClick={() => {
                                    ModalManager.show({
                                      noPadding: true,
                                      size: "sm",
                                      content: (state, setState, close) => (
                                        <RACAccountForm
                                          account={data}
                                          direction={props.asset.data.direction}
                                          entityId={entity}
                                          onClose={close}
                                          onSubmit={(node: InvoiceAccount) => {
                                            close();
                                            reload();
                                          }}
                                          initialValues={{
                                            accountNumber: value,
                                          }}
                                        />
                                      ),
                                    });
                                  }}
                                >
                                  <BfIcon {...DefaultIcons.EDIT} size={"xxs"} />
                                </BFButton>
                              </div>
                            )}
                          />
                        </div>
                      </div>

                      <Field name={`${name}.BUCode`}>
                        {({ input, meta }) => (
                          <>
                            <BFInput
                              noBorderRadiusBottom
                              noBorderRadiusTop
                              prefix={i18n.t(
                                "ra:RABookInvoiceForm.BU",
                                "Buchungsschlüssel"
                              )}
                              removeSuffixPadding
                              suffix={
                                <BFOverlay
                                  trigger="click"
                                  enterable
                                  placement="bottomEnd"
                                  speakerFC={(close) => (
                                    <div>
                                      <BFAssetChooser
                                        height={500}
                                        // value={input.value}
                                        matchQuery={MQ.and(
                                          MQ.eq("data.entity", entity)
                                        )}
                                        focusOnMount
                                        assetType={
                                          AssetTypes.Invoicing.AccountBU
                                        }
                                        getOption={(
                                          node: InvoiceAccountBU
                                        ) => ({
                                          label: node.data.description,
                                          value: node._id,
                                          subLabel: node.data.accountNumber,
                                        })}
                                        onChange={(
                                          value,
                                          asset: InvoiceAccountBU
                                        ) => {
                                          input.onChange(
                                            asset.data.accountNumber
                                          );
                                          close();
                                        }}
                                      />
                                    </div>
                                  )}
                                >
                                  <BFButton
                                    appearance="link"
                                    size="xxs"
                                    onClick={() => {}}
                                  >
                                    <BfIcon
                                      {...DefaultIcons.SEARCH}
                                      size="xxs"
                                    />
                                  </BFButton>
                                </BFOverlay>
                              }
                              {...input}
                              {...FV.getValidation(meta)}
                            />
                          </>
                        )}
                      </Field>

                      <div className={`additional-info-row`}>
                        <div className={`left`} />
                        <div className={`additiona-info-value`}>
                          <DependingCalculationField
                            throttle={500}
                            dependingField={`${name}.BUCode`}
                            name={`${name}.BU`}
                            calulationPromise={async (value) => {
                              const invoiceAccountBU =
                                await CacheService.getData({
                                  assetType: AssetTypes.Invoicing.AccountBU,
                                  query: MQ.and(
                                    MQ.eq("data.accountNumber", value),
                                    MQ.eq("data.entity", entity)
                                  ),
                                  oType: "asset",
                                  forceReload: true,
                                });
                              if (invoiceAccountBU) {
                                return {
                                  value: invoiceAccountBU._id,
                                  data: invoiceAccountBU,
                                };
                              } else {
                                throw new Error(
                                  i18n.t(
                                    "ra:RABookInvoiceForm.BUError",
                                    "Buchungsschlüssel nicht gefunden"
                                  )
                                );
                              }
                            }}
                            renderLoading={() => <Loader size="xs" />}
                            renderError={(error, value: string, reload) => (
                              <div className={`__error`}>
                                {error?.message || "error"}

                                <BFButton
                                  disabled={disableEditAccounting}
                                  tooltip={
                                    disableEditAccounting
                                      ? {
                                          tooltip: i18n.t(
                                            "ra:RABookInvoiceForm.disabledNoPermissionBU",
                                            "Sie haben keine Berechtigung, um Buchungsschlüssel zu erstellen"
                                          ),
                                        }
                                      : undefined
                                  }
                                  appearance="link"
                                  noPadding
                                  size="xs"
                                  onClick={() => {
                                    ModalManager.show({
                                      noPadding: true,
                                      size: "sm",
                                      content: (state, setState, close) => (
                                        <RACAccountBUForm
                                          direction={props.asset.data.direction}
                                          entityId={entity}
                                          onClose={close}
                                          onSubmit={(
                                            node: InvoiceAccountBU
                                          ) => {
                                            props.formProps.change(
                                              `${name}.BUCode`,
                                              node.data.accountNumber
                                            );
                                            props.formProps.change(
                                              `${name}.BU`,
                                              node._id
                                            );
                                            close();
                                            reload();
                                          }}
                                          initialValues={{
                                            accountNumber: value,
                                          }}
                                        />
                                      ),
                                    });
                                  }}
                                >
                                  {i18n.t(
                                    "Global.Buttons.establish",
                                    "Anlegen"
                                  )}
                                </BFButton>
                              </div>
                            )}
                            renderValue={(value, data, reload) => (
                              <div className={`__flex full-width`}>
                                <div className={`__flex-1`}>
                                  {data.data.description}
                                </div>

                                <BFButton
                                  disabled={disableEditAccounting}
                                  tooltip={
                                    disableEditAccounting
                                      ? {
                                          tooltip: i18n.t(
                                            "ra:RABookInvoiceForm.disabledNoPermissionBU",
                                            "Sie haben keine Berechtigung, um Buchungsschlüssel zu erstellen"
                                          ),
                                        }
                                      : undefined
                                  }
                                  appearance="link"
                                  noPadding
                                  className={`padding-5`}
                                  size="xs"
                                  onClick={() => {
                                    ModalManager.show({
                                      noPadding: true,
                                      size: "sm",
                                      content: (state, setState, close) => (
                                        <RACAccountBUForm
                                          accountBU={data}
                                          direction={props.asset.data.direction}
                                          entityId={entity}
                                          onClose={close}
                                          onSubmit={(
                                            node: InvoiceAccountBU
                                          ) => {
                                            close();
                                            reload();
                                          }}
                                        />
                                      ),
                                    });
                                  }}
                                >
                                  <BfIcon {...DefaultIcons.EDIT} size={"xxs"} />
                                </BFButton>
                              </div>
                            )}
                          />
                        </div>
                      </div>

                      <Field
                        name={`${name}.referenceField`}
                        validate={FV.compose(FV.required())}
                      >
                        {({ input, meta }) => (
                          <>
                            <BFInput
                              noBorderRadiusBottom
                              noBorderRadiusTop
                              prefix={i18n.t(
                                "ra:RABookInvoiceForm.referenceField",
                                "Belegfeld 1"
                              )}
                              {...input}
                              {...FV.getValidation(meta)}
                              removeSuffixPadding
                              suffix={
                                fields.length > 1 ? (
                                  <BFButton
                                    appearance="link"
                                    size="sm"
                                    // noPadding
                                    tooltip={{
                                      tooltip: i18n.t(
                                        "ra:RABookInvoiceForm.copyValue",
                                        "Wert in alle Einträge kopieren"
                                      ),
                                    }}
                                    onClick={() => {
                                      props.formProps.batch(() => {
                                        fields.forEach((field) => {
                                          props.formProps.change(
                                            `${field}.referenceField`,
                                            input.value
                                          );
                                        });
                                      });
                                    }}
                                  >
                                    <BfIcon
                                      type="light"
                                      data="duplicate"
                                      size="xxs"
                                    />
                                  </BFButton>
                                ) : undefined
                              }
                            />
                          </>
                        )}
                      </Field>
                      <Field
                        name={`${name}.date`}
                        validate={FV.compose(
                          FV.required(),
                          when(
                            InvoiceStruct.getInvoiceBookingEndDate(entity),
                            (date) =>
                              FV.dateAfter(
                                date,
                                i18n.t(
                                  "ra:RABookInvoiceForm.dateBookingEnd",
                                  "Der Buchungszeitraum des abgeschlossenen Geschäftsjahres ist bereits beendet. Neue Buchungen können ab dem {{dateFrom}} vorgenommen werden.",
                                  { dateFrom: StringUtils.formatDate(date) }
                                )
                              )
                          )
                        )}
                      >
                        {({ input, meta }) => (
                          <>
                            <BFDatefield
                              noBorderRadiusBottom
                              noBorderRadiusTop
                              prefix={i18n.t(
                                "ra:RABookInvoiceForm.date",
                                "Datum"
                              )}
                              {...input}
                              {...FV.getValidation(meta)}
                            />
                          </>
                        )}
                      </Field>
                      <Field name={`${name}.serviceDate`}>
                        {({ input, meta }) => (
                          <>
                            <BFDatefield
                              noBorderRadiusBottom
                              noBorderRadiusTop
                              prefix={i18n.t(
                                "ra:RABookInvoiceForm.serviceDate",
                                "Leistungsdatum"
                              )}
                              {...input}
                              {...FV.getValidation(meta)}
                            />
                          </>
                        )}
                      </Field>
                      <Field name={`${name}.cost1`}>
                        {({ input, meta }) => (
                          <>
                            <BFInput
                              noBorderRadiusBottom
                              noBorderRadiusTop
                              prefix={i18n.t(
                                "ra:RABookInvoiceForm.cost1",
                                "KOST 1"
                              )}
                              {...input}
                              {...FV.getValidation(meta)}
                              removeSuffixPadding
                              suffix={
                                fields.length > 1 ? (
                                  <BFButton
                                    appearance="link"
                                    size="sm"
                                    // noPadding
                                    tooltip={{
                                      tooltip: i18n.t(
                                        "ra:RABookInvoiceForm.copyValue",
                                        "Wert in alle Einträge kopieren"
                                      ),
                                    }}
                                    onClick={() => {
                                      props.formProps.batch(() => {
                                        fields.forEach((field) => {
                                          props.formProps.change(
                                            `${field}.cost1`,
                                            input.value
                                          );
                                        });
                                      });
                                    }}
                                  >
                                    <BfIcon
                                      type="light"
                                      data="duplicate"
                                      size="xxs"
                                    />
                                  </BFButton>
                                ) : undefined
                              }
                            />
                          </>
                        )}
                      </Field>
                      <Field name={`${name}.cost2`}>
                        {({ input, meta }) => (
                          <>
                            <BFInput
                              noBorderRadiusBottom
                              noBorderRadiusTop
                              prefix={i18n.t(
                                "ra:RABookInvoiceForm.cost2",
                                "KOST 2"
                              )}
                              {...input}
                              {...FV.getValidation(meta)}
                              removeSuffixPadding
                              suffix={
                                fields.length > 1 ? (
                                  <BFButton
                                    appearance="link"
                                    size="sm"
                                    // noPadding
                                    tooltip={{
                                      tooltip: i18n.t(
                                        "ra:RABookInvoiceForm.copyValue",
                                        "Wert in alle Einträge kopieren"
                                      ),
                                    }}
                                    onClick={() => {
                                      props.formProps.batch(() => {
                                        fields.forEach((field) => {
                                          props.formProps.change(
                                            `${field}.cost2`,
                                            input.value
                                          );
                                        });
                                      });
                                    }}
                                  >
                                    <BfIcon
                                      type="light"
                                      data="duplicate"
                                      size="xxs"
                                    />
                                  </BFButton>
                                ) : undefined
                              }
                            />
                          </>
                        )}
                      </Field>

                      <FormFieldValues names={[`${name}.extraInfo`]}>
                        {([extraInfo]) => (
                          <Field
                            name={`${name}.usage`}
                            validate={FV.compose(FV.required())}
                          >
                            {({ input, meta }) => (
                              <>
                                <BFInput
                                  noBorderRadiusBottom={extraInfo?.length > 0}
                                  noBorderRadiusTop
                                  prefix={i18n.t(
                                    "ra:RABookInvoiceForm.usage",
                                    "Buchungstext"
                                  )}
                                  {...input}
                                  {...FV.getValidation(meta)}
                                />
                              </>
                            )}
                          </Field>
                        )}
                      </FormFieldValues>
                      <FieldArray<string> name={`${name}.extraInfo`}>
                        {({ fields }) => (
                          <>
                            {fields?.map((name, index) => (
                              <Field
                                name={name}
                                key={index}
                                validate={FV.required()}
                              >
                                {({ input, meta }) => (
                                  <>
                                    <BFInput
                                      noBorderRadiusBottom={
                                        index < fields.length - 1
                                      }
                                      noBorderRadiusTop
                                      prefix={
                                        i18n.t(
                                          "ra:RABookInvoiceForm.extraInfo",
                                          "Zusatzinfo"
                                        ) +
                                        " " +
                                        (index + 1)
                                      }
                                      {...input}
                                      {...FV.getValidation(meta)}
                                      removeSuffixPadding
                                      suffix={
                                        <div>
                                          <BFButton
                                            appearance="link"
                                            size="sm"
                                            onClick={() => fields.remove(index)}
                                          >
                                            <BfIcon
                                              {...DefaultIcons.CLOSE}
                                              size="xxs"
                                            />
                                          </BFButton>
                                        </div>
                                      }
                                    />
                                  </>
                                )}
                              </Field>
                            ))}
                            <div className={`__flex __justify-center`}>
                              <BFButton
                                appearance="link"
                                size="xs"
                                onClick={() => fields.push("")}
                              >
                                {i18n.t(
                                  "ra:RABookInvoiceForm.addExtraInfo",
                                  "Zusatzinfo anlegen"
                                )}
                              </BFButton>
                            </div>
                          </>
                        )}
                      </FieldArray>
                    </div>
                  );
                })}
              </div>
              <div className={`__flex __justify-center padding-bottom-10`}>
                <BFButton
                  appearance="link"
                  size="sm"
                  onClick={() =>
                    fields.push({
                      BU: null,
                      BUCode: "",
                      accountNumberCode: "",
                      accountNumber: null,
                      referenceField: fields.value[0]?.referenceField || "",
                      serviceDate: fields.value[0]?.serviceDate || null,
                      date: fields.value[0]?.date || null,
                      usage: fields.value[0]?.usage || "",
                      cost1: fields.value[0]?.cost1 || "",
                      cost2: "", //fields.value[0]?.cost2 || "",
                      value: openAmount,
                      extraInfo: [],
                    })
                  }
                >
                  {i18n.t(
                    "ra:RABookInvoiceForm.splitBooking",
                    "Buchung aufteilen"
                  )}
                </BFButton>
              </div>
            </>
          );
        }}
      </FieldArray>
    </>
  );
};

const BookingInstallments = (props: {
  prefix?: string;
  formProps: FormApi<BookInvoiceFormData>;
  asset: Invoice;
  amount: number;
  currency: string;
  entity: ObjectId;
}) => {
  // const installment = props.asset.data.installment || [];

  // if (installment.length === 0) return null;

  return (
    <FormFieldValues names={[`${props.prefix || ""}installment`]}>
      {([installment]) =>
        installment?.length > 0 ? (
          <div>
            <div className={`__h4 padding-top-20 padding-bottom-10`}>
              {i18n.t(
                "ra:RABookInvoiceForm.installmentAmountSplit",
                "Abschlag buchen"
              )}
            </div>

            <FieldArray<InvoiceInstallmentForm>
              name={`${props.prefix || ""}installment`}
            >
              {({ fields }) => (
                <>
                  <Accordion bordered defaultActiveKey={1}>
                    {fields.map((name, index) => (
                      <>
                        <Field
                          name={`installmentPlanSumValidation_${index}`}
                          validate={(value, allValues) => {
                            const valuesRoot =
                              (props.prefix
                                ? allValues[
                                    props.prefix.substring(
                                      0,
                                      props.prefix.length - 1
                                    )
                                  ]
                                : allValues) || {};

                            return NumberUtils.normalizeDecimal(
                              _.sum(
                                valuesRoot["installment"]?.[
                                  index
                                ]?.entries?.map((e) => e.value || 0)
                              )
                            ) !==
                              NumberUtils.normalizeDecimal(
                                valuesRoot["installment"]?.[index].amount
                              ) ? (
                              <Trans i18nKey="ra:RABookInvoiceForm.sumValidationWithExplainationForInstallment">
                                Die Summe der Buchung muss der Abschlagssumme
                                entsprechen. Aktuell sind insgesamt{" "}
                                <span className="weight-600">
                                  {{
                                    sum: StringUtils.formatCurrency(
                                      _.sum(
                                        valuesRoot["installment"]?.[
                                          index
                                        ]?.entries?.map((e) => e.value || 0)
                                      ),
                                      undefined,
                                      undefined,
                                      props.currency
                                    ),
                                  }}{" "}
                                  gebucht,
                                </span>{" "}
                                es sind noch{" "}
                                <span className="weight-600">
                                  {{
                                    missing: StringUtils.formatCurrency(
                                      valuesRoot["installment"]?.[index]
                                        .amount -
                                        _.sum(
                                          valuesRoot?.[`installment`]?.[
                                            index
                                          ]?.entries?.map((e) => e.value || 0)
                                        ),
                                      undefined,
                                      undefined,
                                      props.currency
                                    ),
                                  }}{" "}
                                  offen.
                                </span>
                              </Trans>
                            ) : undefined;
                          }}
                        >
                          {({ input, meta }) => {
                            const error = props.prefix
                              ? meta.error
                              : meta.error?.[
                                  props.prefix.substring(
                                    0,
                                    props.prefix.length - 1
                                  )
                                ];

                            return (
                              <FormSpy>
                                {({ errors }) => {
                                  const hasAnyChildErrors =
                                    errors?.installment?.[index]?.entries;
                                  return (
                                    <FormFieldValues
                                      names={[`${name}.amount`, `${name}.date`]}
                                    >
                                      {([amount, date]) => (
                                        <Accordion.Panel
                                          header={
                                            <div
                                              className={classNames(
                                                `installment-header __flex full-width __align-center padding-right-10`,
                                                {
                                                  error:
                                                    Boolean(error) ||
                                                    hasAnyChildErrors,
                                                }
                                              )}
                                            >
                                              <div className={`__h3`}>
                                                {i18n.t(
                                                  "ra:RABookInvoiceForm.installment",
                                                  "Abschlag"
                                                )}{" "}
                                                {index + 1}
                                              </div>

                                              <div className={`__sub-text`}>
                                                {StringUtils.formatDate(date)}
                                              </div>
                                              <div
                                                className={`__flex-1 __flex __justify-end`}
                                              >
                                                {StringUtils.formatCurrency(
                                                  amount,
                                                  undefined,
                                                  undefined,
                                                  props.asset.data.invoice.value
                                                    .currency
                                                )}
                                              </div>
                                            </div>
                                          }
                                          key={index}
                                          eventKey={index + 1}
                                        >
                                          <>
                                            {Boolean(error) && (
                                              <BFMessageBox
                                                className={`margin-bottom-10`}
                                                type="error"
                                              >
                                                {meta.error}
                                              </BFMessageBox>
                                            )}

                                            <div
                                              className={`__flex __justify-end`}
                                            >
                                              <BFButton
                                                size="xs"
                                                appearance="link"
                                                onClick={() => {
                                                  props.formProps.batch(() => {
                                                    const value =
                                                      fields.value[index];
                                                    const entries =
                                                      value?.entries || [];

                                                    const percentages =
                                                      entries.map(
                                                        (e) =>
                                                          e.value / value.amount
                                                      );

                                                    fields.value.forEach(
                                                      (entry, i) => {
                                                        if (i === index) return;

                                                        const newEntries =
                                                          entries.map(
                                                            (e, j) => {
                                                              return {
                                                                ...e,
                                                                value:
                                                                  entry.amount *
                                                                  percentages[
                                                                    j
                                                                  ],
                                                                date: entry
                                                                  .entries[0]
                                                                  .date,
                                                                serviceDate:
                                                                  entry
                                                                    .entries[0]
                                                                    .serviceDate,
                                                              };
                                                            }
                                                          );
                                                        props.formProps.change(
                                                          `${
                                                            props.prefix || ""
                                                          }installment[${i}].entries`,
                                                          newEntries
                                                        );
                                                      }
                                                    );
                                                  });
                                                }}
                                              >
                                                {i18n.t(
                                                  "ra:RABookInvoiceForm.copyValues",
                                                  "Daten in alle Einträge kopieren"
                                                )}
                                              </BFButton>
                                            </div>
                                            <BookingPayments
                                              name={`${name}.entries`}
                                              asset={props.asset}
                                              formProps={props.formProps}
                                              baseAmount={amount}
                                              entity={props.entity}
                                            />
                                          </>
                                        </Accordion.Panel>
                                      )}
                                    </FormFieldValues>
                                  );
                                }}
                              </FormSpy>
                            );
                          }}
                        </Field>
                      </>
                    ))}
                  </Accordion>
                </>
              )}
            </FieldArray>
            {/* <BookingPayments
        name="installment"
        asset={props.asset}
        formProps={formProps.form}
      /> */}
          </div>
        ) : null
      }
    </FormFieldValues>
  );
};
